import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Card } from "reactstrap";

import { Block, BlockBetween, BlockTitle, InputSwitch } from "../../Component";
import RSelect from "../../select/ReactSelect";
import UpdatableText from "../../shared/UpdatabaleText";
import BlockUpdateButton from "../../button/BlockUpdateButton";

import partnerService from "../../../services/partnerService";
import { useBeManualFetcher } from "../../../hooks/useBeManualFetcher";
import { usePartnerPageContext } from "../../../hooks/usePartnerPageContext";
import EventEmitter, { EVENT_TOPICS } from "../../../utilities/eventUtils";
import {
  LANGUAGE_KEYS,
  PARTNER_CATEGORIES,
  PARTNER_CATEGORIES_READABLE,
} from "../../../utilities/constants";

const categoryOptions = Object.values(PARTNER_CATEGORIES).map((category) => ({
  value: category,
  label: PARTNER_CATEGORIES_READABLE[category],
}));

const PartnerMainInfoBlock = () => {
  const { partnerId } = useParams();
  const { register, handleSubmit } = useForm();
  const [onFetch] = useBeManualFetcher();
  const { singlePartner } = usePartnerPageContext();

  const [hasEditClicked, setHasEditClicked] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showInHomeSlider, setShowInHomeSlider] = useState(
    singlePartner?.showInHomeSlider
  );
  const [isActive, setIsActive] = useState(singlePartner?.isActive);

  function handleOnEditClicked() {
    setHasEditClicked((prev) => !prev);
  }

  async function onFormSubmit(form) {
    const { uzName, ruName, enName, phoneNumber, orderIndex } = form;

    const data = {
      category: selectedCategory?.value ?? singlePartner?.category,
      name: {
        [LANGUAGE_KEYS.UZ]: uzName,
        [LANGUAGE_KEYS.RU]: ruName,
        [LANGUAGE_KEYS.EN]: enName,
      },
      orderIndex: orderIndex ? Number(orderIndex) : null,
      showInHomeSlider,
      isActive,
      phoneNumber,
    };

    return await onFetch({
      action: () => partnerService.updatePartner(partnerId, { data }),
      onLoad: (updatedPartner) => {
        EventEmitter.emit(EVENT_TOPICS.ON_PARTNER_UPDATED, { updatedPartner });
        handleOnEditClicked();
      },
      successMessage: "Partner updated successfully",
    });
  }

  return (
    <Card className="card-inner">
      <Block>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <BlockBetween>
            <BlockTitle tag="h5">Main Information</BlockTitle>
            <BlockUpdateButton
              hasEditClicked={hasEditClicked}
              handleOnEditClicked={handleOnEditClicked}
            />
          </BlockBetween>
          <div className="profile-ud-list">
            <div className="profile-ud-item mr-5">
              <UpdatableText
                title="Name (O'zbekcha)"
                defaultValue={singlePartner?.name?.[LANGUAGE_KEYS.UZ]}
                name={"uzName"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Name (На русском)"
                defaultValue={singlePartner?.name?.[LANGUAGE_KEYS.RU]}
                name={"ruName"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Name (In English)"
                defaultValue={singlePartner?.name?.[LANGUAGE_KEYS.EN]}
                name={"enName"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Phone Number"
                defaultValue={singlePartner?.phoneNumber}
                name={"phoneNumber"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
            </div>
            <div className="profile-ud-item">
              <UpdatableText
                title="Category"
                defaultValue={
                  PARTNER_CATEGORIES_READABLE[singlePartner?.category] ??
                  singlePartner?.category
                }
                editableValueComponent={
                  <RSelect
                    options={categoryOptions}
                    value={
                      selectedCategory ??
                      categoryOptions.find(
                        (option) => option.value === singlePartner?.category
                      )
                    }
                    onChange={(option) => setSelectedCategory(option)}
                    placeholder="Select category"
                  />
                }
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Order Index"
                type="number"
                defaultValue={singlePartner?.orderIndex}
                name={"orderIndex"}
                register={register}
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Show In Home Slider"
                defaultValue={
                  <InputSwitch
                    id="showInHomeSliderView"
                    checked={singlePartner?.showInHomeSlider}
                  />
                }
                editableValueComponent={
                  <InputSwitch
                    id="showInHomeSliderEdit"
                    onClick={() => setShowInHomeSlider((prev) => !prev)}
                    checked={showInHomeSlider}
                  />
                }
                onEditClicked={hasEditClicked}
                isWider
              />
              <UpdatableText
                title="Is Active"
                defaultValue={
                  <InputSwitch
                    id="isActiveView"
                    checked={singlePartner?.isActive}
                  />
                }
                editableValueComponent={
                  <InputSwitch
                    id="isActiveEdit"
                    onClick={() => setIsActive((prev) => !prev)}
                    checked={isActive}
                  />
                }
                onEditClicked={hasEditClicked}
                isWider
              />
            </div>
          </div>
        </form>
      </Block>
    </Card>
  );
};

export default PartnerMainInfoBlock;
